/**
 * Keyboard Help Modal
 * Displays all available keyboard shortcuts
 */

import React, { useEffect, useCallback } from 'react';
import { KEYBOARD_HELP } from '../hooks/useKeyboardNav';

const KeyboardHelp = ({ onClose }) => {
  // Close on Escape
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' || e.key === '?') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Render key badge
  const renderKey = (key) => {
    const specialKeys = {
      '⌘': 'Cmd',
      '⇧': 'Shift',
      '↵': 'Enter',
      '⌫': 'Backspace'
    };

    return (
      <kbd className="inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 text-xs font-mono font-semibold text-gray-700 bg-gray-100 border border-gray-300 rounded shadow-sm">
        {specialKeys[key] || key}
      </kbd>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Keyboard Shortcuts</h2>
              <p className="text-sm text-gray-500">Navigate and review faster with these shortcuts</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {KEYBOARD_HELP.map((section) => (
              <div key={section.category}>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">
                  {section.category}
                </h3>
                <div className="space-y-2">
                  {section.shortcuts.map((shortcut, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50"
                    >
                      <span className="text-sm text-gray-600">
                        {shortcut.description}
                      </span>
                      <div className="flex items-center gap-1">
                        {shortcut.keys.map((key, keyIdx) => (
                          <React.Fragment key={keyIdx}>
                            {keyIdx > 0 && <span className="text-gray-400 text-xs mx-1">or</span>}
                            <div className="flex items-center gap-0.5">
                              {key.split('+').map((part, partIdx) => (
                                <React.Fragment key={partIdx}>
                                  {partIdx > 0 && <span className="text-gray-400 text-xs">+</span>}
                                  {renderKey(part)}
                                </React.Fragment>
                              ))}
                              {key.includes(' ') && !key.includes('+') && (
                                <span className="text-gray-400 text-xs ml-1">(sequence)</span>
                              )}
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>Press <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs font-mono">?</kbd> or <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs font-mono">Esc</kbd> to close</span>
            <span>Shortcuts work when not editing text fields</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyboardHelp;
